import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Box, Button, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EventIcon from "@mui/icons-material/Event";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { tvNavyTextMuted } from "../utils/authBranding";
import { useTvBackPath, getTvBackLabel } from "../utils/tvNavigation";
import { AUTOMATION_ACCENT } from "../utils/accents";

export const MEALS_PAGE_ACCENTS = {
  recipes: "#81c784",
  lists: "#c9a0ff",
  calendar: "#60a5fa",
  automation: AUTOMATION_ACCENT,
  plan: "#ffb74d",
};

export const mealsPageShellSx = {
  width: "100%",
  maxWidth: 720,
  mx: "auto",
  boxSizing: "border-box",
};

export const mealsPageFrameSx = {
  ...mealsPageShellSx,
  px: { xs: 2, sm: 3 },
  pt: { xs: 2, sm: 3 },
  pb: { xs: 11, sm: 5 },
};

const HOME_BUTTON_HIDDEN_PREFIXES = ["/onboarding", "/invite", "/shared", "/tv"];
const HOME_BUTTON_HIDDEN_PATHS = new Set([
  "/",
  "/grocery",
  "/calendar",
  "/sign-in",
  "/sign-up",
  "/privacy",
  "/terms",
  "/delete-account",
]);

export function shouldShowMobileHome(pathname) {
  const path = String(pathname || "").split("?")[0];
  if (HOME_BUTTON_HIDDEN_PATHS.has(path)) return false;
  return !HOME_BUTTON_HIDDEN_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** Thumb-sized home circle — iPhone home button, back to the dashboard. */
export function MobileHomeButton() {
  const navigate = useNavigate();
  return (
    <Box
      component="button"
      type="button"
      aria-label="Home"
      onClick={() => navigate("/")}
      sx={{
        position: "fixed",
        left: "50%",
        bottom: "calc(14px + env(safe-area-inset-bottom))",
        transform: "translateX(-50%)",
        width: 62,
        height: 62,
        p: 0,
        m: 0,
        borderRadius: "50%",
        zIndex: 24,
        cursor: "pointer",
        appearance: "none",
        WebkitAppearance: "none",
        color: "transparent",
        bgcolor: "#101820",
        border: "2px solid rgba(220, 228, 238, 0.42)",
        boxShadow: [
          "0 10px 28px rgba(0,0,0,0.48)",
          "inset 0 1px 1px rgba(255,255,255,0.22)",
          "inset 0 -8px 14px rgba(0,0,0,0.45)",
        ].join(", "),
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 11,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "inset 0 2px 6px rgba(0,0,0,0.5)",
          pointerEvents: "none",
        },
        "&:hover": {
          bgcolor: "#15202c",
          borderColor: "rgba(220, 228, 238, 0.55)",
        },
        "&:active": {
          transform: "translateX(-50%) scale(0.94)",
        },
      }}
    />
  );
}

/** Compact Lifestack header used across meals surfaces. */
export function MealsPageHeader({
  title,
  subtitle = null,
  kicker = "Lifestack",
  backTo,
  onBack,
  actions = null,
  status = null,
  mb = { xs: 3, sm: 4 },
}) {
  const navigate = useNavigate();
  const tvBack = useTvBackPath("/");
  const backPath = backTo || tvBack;
  const backLabel = onBack ? "Back" : getTvBackLabel(backPath);

  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    if (typeof backPath === "string") navigate(backPath);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        mb,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <IconButton
          onClick={handleBack}
          aria-label={backLabel}
          sx={{
            color: "rgba(255,255,255,0.75)",
            border: "1px solid rgba(255,255,255,0.14)",
            borderRadius: 2,
            "&:hover": {
              color: "#fff",
              borderColor: "rgba(255,255,255,0.28)",
              bgcolor: "rgba(255,255,255,0.04)",
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          {kicker ? (
            <Typography
              variant="overline"
              sx={{
                display: "block",
                lineHeight: 1.2,
                letterSpacing: "0.14em",
                color: tvNavyTextMuted,
                fontWeight: 700,
              }}
            >
              {kicker}
            </Typography>
          ) : null}
          <Typography
            variant="h6"
            fontWeight={700}
            noWrap
            sx={{ lineHeight: 1.15, color: "#fff" }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              variant="caption"
              noWrap
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                mt: kicker ? 0.25 : 0.15,
                color: tvNavyTextMuted,
                fontWeight: 700,
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {(actions || status) && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {status}
          {actions}
        </Stack>
      )}
    </Box>
  );
}

export function MealsSectionLabel({ children, sx }) {
  return (
    <Typography
      variant="caption"
      fontWeight={700}
      sx={{
        display: "block",
        color: tvNavyTextMuted,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        mb: 1.25,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

export function MealsQuietPanel({ children, accent, sx }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? `${accent}44` : "rgba(255,255,255,0.1)",
        bgcolor: accent ? `${accent}12` : "rgba(255,255,255,0.03)",
        p: { xs: 1.75, sm: 2 },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const BROWSE_ITEMS = [
  { id: "home", label: "Home", href: "/", icon: HomeOutlinedIcon },
  { id: "recipes", label: "Recipes", href: "/recipes", icon: MenuBookIcon, accent: MEALS_PAGE_ACCENTS.recipes },
  {
    id: "calendar",
    label: "Calendar",
    href: "/calendar?filter=meals&view=week",
    icon: EventIcon,
    accent: MEALS_PAGE_ACCENTS.calendar,
  },
  {
    id: "lists",
    label: "Lists",
    href: "/grocery/lists",
    icon: ShoppingCartOutlinedIcon,
    accent: MEALS_PAGE_ACCENTS.lists,
  },
  {
    id: "automation",
    label: "Automation",
    href: "/automation",
    icon: AutoAwesomeIcon,
    accent: MEALS_PAGE_ACCENTS.automation,
  },
];

/** Profile-only legal / help browse cards. */
export const PROFILE_BROWSE_EXTRA = [
  { id: "terms", label: "Terms", href: "/terms", icon: GavelOutlinedIcon },
  { id: "privacy", label: "Privacy", href: "/privacy", icon: PrivacyTipOutlinedIcon },
  { id: "help", label: "Help", href: "/help", icon: HelpOutlineIcon },
];

/** Quiet text/icon navigation row — exclude the current page. */
export function MealsBrowseShortcuts({ current, accent, extraItems = [] }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  if (isMobile) return null;

  const items = [
    ...BROWSE_ITEMS.filter((item) => item.id !== current),
    ...(Array.isArray(extraItems) ? extraItems : []),
  ];
  const fallbackAccent = accent || MEALS_PAGE_ACCENTS.plan;
  const columns = Math.min(Math.max(items.length, 1), 4);

  return (
    <Box sx={{ mt: 3 }}>
      <MealsSectionLabel>Browse</MealsSectionLabel>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: { xs: 0.75, sm: 1 },
        }}
      >
        {items.map((item) => {
          const ItemIcon = item.icon;
          const itemAccent = item.accent || fallbackAccent;
          return (
            <Button
              key={item.id}
              component={RouterLink}
              to={item.href}
              sx={{
                flexDirection: "column",
                gap: 0.75,
                py: 1.5,
                px: 0.75,
                minWidth: 0,
                textTransform: "none",
                color: "rgba(255,255,255,0.85)",
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.1)",
                bgcolor: "transparent",
                "&:hover": {
                  bgcolor: `${itemAccent}14`,
                  borderColor: `${itemAccent}55`,
                  color: "#fff",
                },
              }}
            >
              <Box sx={{ color: itemAccent, display: "flex" }}>
                <ItemIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {item.label}
              </Typography>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}
